import {
    InteractionRequiredAuthError,
    type AuthenticationResult,
    type PublicClientApplication,
} from "@azure/msal-browser";
import type { AppConfig } from "./authConfig";
import { clearLoginHint, persistLoginHint, readLoginHint } from "./loginHintStore";

/**
 * Resolve the active MSAL account at app boot, attempting a silent, UI-less
 * SSO renewal before falling back to the interactive sign-in gate.
 *
 * Runs once in `main.tsx` after `instance.initialize()`, before React mounts.
 * It is the single place that decides "do we already have a usable session?"
 * and it is structurally total: it **never throws**. Any failure degrades to
 * "no active account", and the app renders `SignInGate` — boot must always
 * reach `createRoot`.
 *
 * Resolution order (first match wins):
 *   1. A returning interactive redirect (the user just came back from
 *      Microsoft via `loginRedirect` / `acquireTokenRedirect`).
 *   2. An account already in MSAL's cache (a warm tab whose `sessionStorage`
 *      still holds the account).
 *   3. Silent SSO (`ssoSilent`) against a live Entra session, targeted by the
 *      login hint persisted from a prior sign-in. This is the path that
 *      removes the button click on a cold tab.
 *
 * When none apply, the function returns with no active account set and the
 * gate's button is the (correct, expected) fallback — notably on the very
 * first sign-in (no hint yet) and wherever hidden-iframe SSO is blocked
 * (third-party cookies, no live session, consent/MFA step-up).
 */
export async function bootstrapAuth(
    instance: PublicClientApplication,
    config: AppConfig,
): Promise<void> {
    // 1. Consume a returning interactive redirect, if any.
    //
    // handleRedirectPromise() rejects when a returning hash fails to validate —
    // an Entra error response, a state/nonce mismatch, or (on this shared
    // origin) a flow started by a previous shell and completed by this one.
    // This await is the one place bootstrap can throw, and an unhandled throw
    // here propagates through main.tsx's top-level await and prevents
    // createRoot().render() from ever running — a black screen on exactly the
    // post-login navigation. Bootstrap is total by contract: a failed redirect
    // must degrade to "no active account" so the sign-in gate renders. Drop the
    // hint so the next attempt is a clean interactive sign-in rather than a
    // doomed silent retry, and clear any stale interaction state MSAL may have
    // left behind so the gate's loginRedirect is not rejected as
    // "interaction_in_progress".
    let redirectResponse: AuthenticationResult | null;
    try {
        redirectResponse = await instance.handleRedirectPromise();
    } catch (error) {
        console.error("[auth] handleRedirectPromise failed; falling back to sign-in gate", error);
        clearLoginHint();
        redirectResponse = null;
    }
    if (redirectResponse?.account) {
        instance.setActiveAccount(redirectResponse.account);
        persistLoginHint(redirectResponse.account.username);
        return;
    }

    // 2. Re-establish the active account from MSAL's own cache (warm tab).
    if (instance.getActiveAccount() === null) {
        const cached = instance.getAllAccounts()[0];
        if (cached !== undefined) {
            instance.setActiveAccount(cached);
        }
    }
    const active = instance.getActiveAccount();
    if (active !== null) {
        persistLoginHint(active.username);
        return;
    }

    // 3. Cold tab: the session cache is empty, but the user may still hold a
    //    live Entra session. Without a hint there is nothing to silently
    //    target, so the first-ever sign-in deliberately goes through the
    //    button (which then persists the hint for subsequent cold tabs).
    const loginHint = readLoginHint();
    if (loginHint === null) {
        return;
    }

    try {
        const result: AuthenticationResult = await instance.ssoSilent({
            scopes: [config.msal.apiScope],
            loginHint,
        });
        instance.setActiveAccount(result.account);
        persistLoginHint(result.account.username);
    } catch (error) {
        // Persistent "must interact" (no live session, consent/MFA required,
        // or iframe SSO blocked) means the hint can't silently renew — drop
        // it so we don't retry a doomed silent call every boot, and let the
        // gate take over. Transient failures (network, iframe timeout) keep
        // the hint for the next attempt. Either way, boot does not fail.
        if (error instanceof InteractionRequiredAuthError) {
            clearLoginHint();
        }
    }
}
